/* global customElements, HTMLElement */
(function () {
  'use strict';

  const STORAGE_KEY = 'bundle-builder-state';
  const STATE_VERSION = 2;

  function uuid() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
    return 'bdl-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
  }

  function formatMoney(cents) {
    // Theme exposes moneyWithCurrencyFormat; fall back to a simple EUR format.
    const amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de-DE', {
        style: 'currency',
        currency: (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR'
      }).format(amount);
    } catch (err) {
      return amount.toFixed(2) + ' €';
    }
  }

  class BundleBuilder extends HTMLElement {
    constructor() {
      super();
      this.onCardClick = this.onCardClick.bind(this);
      this.onCardKeydown = this.onCardKeydown.bind(this);
      this.onStepperClick = this.onStepperClick.bind(this);
      this.onFooterClick = this.onFooterClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
    }

    connectedCallback() {
      this.carrierVariantId = this.dataset.carrierVariantId || null;
      this.fixedVariantId = this.dataset.fixedVariantId || null;
      this.sectionId = this.dataset.sectionId;
      this.stepEls = Array.from(this.querySelectorAll('.bundle-builder__step'));
      this.selectionSteps = this.stepEls.filter(
        (el) => el.dataset.stepType === 'selection'
      );
      this.summaryStep = this.stepEls.find((el) => el.dataset.stepType === 'summary');
      this.stepperItems = Array.from(this.querySelectorAll('.bundle-builder__stepper-item'));
      this.footerEl = this.querySelector('[data-footer]');
      this.nextBtn = this.querySelector('[data-action="next"]');
      this.backBtn = this.querySelector('[data-action="back"]');
      this.skipBtn = this.querySelector('[data-action="skip"]');
      this.ctaBtn = this.querySelector('[data-action="add-to-cart"]');
      this.errorEl = this.querySelector('[data-error]');
      this.footerCountEl = this.querySelector('[data-footer-count]');
      this.footerTotalEl = this.querySelector('[data-footer-total]');
      this.footerCompareEl = this.querySelector('[data-footer-compare]');
      this.footerSavingsEl = this.querySelector('[data-footer-savings]');
      this.progressCurrentEl = this.querySelector('[data-progress-current]');
      this.progressFillEl = this.querySelector('[data-progress-fill]');
      this.summaryListEl = this.querySelector('[data-summary-list]');
      this.summaryRegularRow = this.querySelector('[data-summary-regular-row]');
      this.summaryRegularEl = this.querySelector('[data-summary-regular]');
      this.summarySavingsRow = this.querySelector('[data-summary-savings-row]');
      this.summarySavingsEl = this.querySelector('[data-summary-savings]');
      this.summaryDiscountRow = this.querySelector('[data-summary-discount-row]');
      this.summaryDiscountEl = this.querySelector('[data-summary-discount]');
      this.summaryTotalEl = this.querySelector('[data-summary-total]');
      this.discountBannerEl = this.querySelector('[data-discount-banner]');
      this.discountLabelEl = this.querySelector('[data-discount-label]');
      this.discountValueEl = this.querySelector('[data-discount-value]');

      this.discount2 = parseFloat(this.dataset.discount2) || 0;
      this.discount3 = parseFloat(this.dataset.discount3) || 0;
      this.discount4 = parseFloat(this.dataset.discount4) || 0;
      this.totalSteps = this.selectionSteps.length + (this.summaryStep ? 1 : 0);

      this.state = this.loadState();
      this.reconcileStateWithDom();

      this.bindEvents();
      this.observeFooterHeight();

      const urlStep = this.readStepFromUrl();
      const initialStep = urlStep != null ? urlStep : this.state.currentStep || 0;
      this.goToStep(initialStep, { replace: true, emit: true });
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      if (this.footerResizeObserver) {
        this.footerResizeObserver.disconnect();
        this.footerResizeObserver = null;
      }
      if (this.footerResizeHandler) {
        window.removeEventListener('resize', this.footerResizeHandler);
        this.footerResizeHandler = null;
      }
    }

    observeFooterHeight() {
      if (!this.footerEl) return;

      const update = () => {
        const h = this.footerEl.offsetHeight;
        if (h > 0) this.style.setProperty('--bb-footer-size', h + 'px');
      };

      update();

      if (typeof ResizeObserver !== 'undefined') {
        this.footerResizeObserver = new ResizeObserver(update);
        this.footerResizeObserver.observe(this.footerEl);
      } else {
        this.footerResizeHandler = update;
        window.addEventListener('resize', this.footerResizeHandler);
      }
    }

    /* ---------------- State ---------------- */

    loadState() {
      const empty = {
        v: STATE_VERSION,
        bundleId: uuid(),
        currentStep: 0,
        selections: {}
      };
      try {
        const raw = sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return empty;
        const parsed = JSON.parse(raw);
        if (!parsed || parsed.v !== STATE_VERSION) return empty;
        parsed.selections = parsed.selections || {};
        return parsed;
      } catch (err) {
        return empty;
      }
    }

    saveState() {
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
      } catch (err) {
        /* quota or disabled — ignore */
      }
    }

    /**
     * Remove selections that reference products or variants no longer in the DOM.
     */
    reconcileStateWithDom() {
      const validStepIndexes = new Set(this.selectionSteps.map((el) => el.dataset.stepIndex));
      Object.keys(this.state.selections).forEach((stepIdx) => {
        if (!validStepIndexes.has(String(stepIdx))) {
          delete this.state.selections[stepIdx];
          return;
        }
        const sel = this.state.selections[stepIdx];
        if (!sel || sel.skipped) return;
        const stepEl = this.selectionSteps.find(
          (el) => el.dataset.stepIndex === String(stepIdx)
        );
        if (!stepEl) return;

        if (sel.multi && Array.isArray(sel.items)) {
          sel.items = sel.items.filter((item) =>
            stepEl.querySelector(
              '.bundle-builder__card[data-variant-id="' +
                String(item.variantId).replace(/"/g, '\\"') +
                '"]'
            )
          );
          if (sel.items.length === 0) {
            delete this.state.selections[stepIdx];
          }
          return;
        }

        if (!sel.variantId) return;
        const variantCard = stepEl.querySelector(
          '.bundle-builder__card[data-variant-id="' +
            String(sel.variantId).replace(/"/g, '\\"') +
            '"]'
        );
        if (!variantCard) {
          delete this.state.selections[stepIdx];
        }
      });
    }

    /* ---------------- Default selections ---------------- */

    initDefaultSelections() {
      this.selectionSteps.forEach((stepEl, idx) => {
        if (this.state.selections[idx]) return;
        const firstCard = stepEl.querySelector(
          '.bundle-builder__card:not([aria-disabled="true"])'
        );
        if (firstCard) this.selectCard(firstCard);
      });
    }

    /* ---------------- URL / history ---------------- */

    readStepFromUrl() {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('step');
      if (raw === null) return null;
      const n = parseInt(raw, 10);
      if (Number.isNaN(n) || n < 0 || n >= this.totalSteps) return null;
      return n;
    }

    writeStepToUrl(step, replace) {
      const url = new URL(window.location.href);
      url.searchParams.set('step', String(step));
      const method = replace ? 'replaceState' : 'pushState';
      history[method]({ bundleStep: step }, '', url.toString());
    }

    onPopState() {
      const urlStep = this.readStepFromUrl();
      if (urlStep != null) {
        this.goToStep(urlStep, { skipHistory: true, emit: true });
      }
    }

    /* ---------------- Event binding ---------------- */

    bindEvents() {
      this.querySelectorAll('.bundle-builder__card').forEach((card) => {
        card.addEventListener('click', this.onCardClick);
        card.addEventListener('keydown', this.onCardKeydown);
      });
      this.stepperItems.forEach((btn) => {
        btn.addEventListener('click', this.onStepperClick);
      });
      if (this.footerEl) {
        this.footerEl.addEventListener('click', this.onFooterClick);
      }
      window.addEventListener('popstate', this.onPopState);
    }

    /* ---------------- Step navigation ---------------- */

    goToStep(step, opts) {
      opts = opts || {};
      step = Math.max(0, Math.min(step, this.totalSteps - 1));

      this.stepEls.forEach((el) => {
        const elStep = parseInt(el.dataset.stepIndex, 10);
        el.hidden = elStep !== step;
      });

      this.stepperItems.forEach((item) => {
        const itemStep = parseInt(item.dataset.stepIndex, 10);
        const isActive = itemStep === step;
        item.setAttribute('aria-selected', isActive ? 'true' : 'false');
        item.tabIndex = isActive ? 0 : -1;
        const hasSelection =
          this.state.selections[itemStep] &&
          (this.state.selections[itemStep].variantId || this.state.selections[itemStep].skipped);
        item.dataset.completed = hasSelection ? 'true' : 'false';
      });

      this.state.currentStep = step;
      this.saveState();

      if (!opts.skipHistory) {
        this.writeStepToUrl(step, !!opts.replace);
      }

      this.syncUi();

      if (this.isSummaryStep(step)) {
        this.renderSummary();
      }

      if (opts.emit !== false) {
        this.emit('bundle:step_viewed', {
          step: step,
          stepName: this.getStepName(step)
        });
      }

      // Move focus to the step heading for screen readers on explicit step
      // changes only. Skipping this on initial mount (replace) and on
      // history navigation avoids a visible focus ring on page refresh.
      if (!opts.replace && !opts.skipHistory) {
        const activeStepEl = this.stepEls.find((el) => parseInt(el.dataset.stepIndex, 10) === step);
        if (activeStepEl) {
          const heading = activeStepEl.querySelector('.bundle-builder__step-heading');
          if (heading) {
            heading.setAttribute('tabindex', '-1');
            try { heading.focus({ preventScroll: true }); } catch (e) { /* no-op */ }
          }
        }
      }

      if (this.progressCurrentEl) this.progressCurrentEl.textContent = String(step + 1);

      if (this.progressFillEl && this.totalSteps > 1) {
        const pct = ((step + 1) / this.totalSteps) * 100;
        this.progressFillEl.style.setProperty('--bb-progress', pct + '%');
      }

      // Scroll the builder into view so the newly-active step lands at the top.
      // Skipped on the initial render (opts.replace) and on history navigation,
      // so we don't hijack the user's scroll position unexpectedly.
      if (!opts.replace && !opts.skipHistory) {
        this.scrollIntoViewTop();
      }
    }

    scrollIntoViewTop() {
      const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const rect = this.getBoundingClientRect();
      const headerOffset = this.getStickyHeaderOffset();
      const target = rect.top + window.scrollY - headerOffset - 16;
      try {
        window.scrollTo({
          top: Math.max(0, target),
          behavior: reduced ? 'auto' : 'smooth'
        });
      } catch (e) {
        window.scrollTo(0, Math.max(0, target));
      }
    }

    getStickyHeaderOffset() {
      // Try common theme patterns for sticky header height.
      const candidates = [
        '.header--sticky',
        'sticky-header',
        '.shopify-section-header-sticky',
        '.section-header.shopify-section-group-header-group',
        '.site-header',
        'header[role="banner"]'
      ];
      for (let i = 0; i < candidates.length; i += 1) {
        const el = document.querySelector(candidates[i]);
        if (el) {
          const r = el.getBoundingClientRect();
          if (r.height > 0 && r.top >= 0 && r.top < 80) return r.height;
        }
      }
      return 0;
    }

    isSummaryStep(step) {
      return this.summaryStep && parseInt(this.summaryStep.dataset.stepIndex, 10) === step;
    }

    getStepName(step) {
      if (this.isSummaryStep(step)) return 'summary';
      const el = this.selectionSteps[step];
      if (!el) return 'step-' + step;
      const heading = el.querySelector('.bundle-builder__step-heading');
      return heading ? heading.textContent.trim() : 'step-' + step;
    }

    /* ---------------- Selection handlers ---------------- */

    onCardClick(evt) {
      const card = evt.currentTarget;
      if (card.getAttribute('aria-disabled') === 'true') return;
      this.selectCard(card);
    }

    onCardKeydown(evt) {
      if (evt.key === 'Enter' || evt.key === ' ') {
        evt.preventDefault();
        this.selectCard(evt.currentTarget);
      }
    }

    buildItemFromCard(card) {
      return {
        productId: card.dataset.productId,
        productTitle: card.dataset.productTitle,
        productHandle: card.dataset.productHandle,
        productUrl: card.dataset.productUrl,
        productImage: this.getCardImageSrc(card),
        variantId: card.dataset.variantId,
        variantTitle: card.dataset.variantTitle || '',
        price: parseInt(card.dataset.variantPrice, 10) || 0,
        comparePrice: parseInt(card.dataset.variantComparePrice, 10) || 0
      };
    }

    selectCard(card) {
      if (card.getAttribute('aria-disabled') === 'true') return;
      const stepEl = card.closest('.bundle-builder__step');
      if (!stepEl) return;

      const stepIdx = parseInt(stepEl.dataset.stepIndex, 10);
      const isMulti = stepEl.dataset.selectionMode === 'multi';
      const role = stepEl.dataset.role || 'accessory';
      const item = this.buildItemFromCard(card);

      if (isMulti) {
        const existing = this.state.selections[stepIdx];
        const items = existing && Array.isArray(existing.items) ? existing.items.slice() : [];
        const idx = items.findIndex((it) => String(it.variantId) === String(item.variantId));
        let nowChecked;
        if (idx >= 0) {
          items.splice(idx, 1);
          nowChecked = false;
        } else {
          items.push(item);
          nowChecked = true;
        }
        card.setAttribute('aria-checked', nowChecked ? 'true' : 'false');

        this.state.selections[stepIdx] = {
          multi: true,
          role: role,
          skipped: false,
          items: items
        };
      } else {
        const alreadySelected = card.getAttribute('aria-checked') === 'true';
        if (alreadySelected) {
          // Toggle off: deselect this card and clear the step's selection
          card.setAttribute('aria-checked', 'false');
          delete this.state.selections[stepIdx];
        } else {
          // Select: clear previous selection in this step, then select this card
          stepEl.querySelectorAll('.bundle-builder__card[aria-checked="true"]').forEach((c) => {
            if (c !== card) c.setAttribute('aria-checked', 'false');
          });
          card.setAttribute('aria-checked', 'true');

          this.state.selections[stepIdx] = Object.assign({}, item, {
            multi: false,
            role: role,
            skipped: false
          });
        }
      }

      this.saveState();

      this.emit('bundle:product_selected', {
        step: stepIdx,
        productId: card.dataset.productId,
        variantId: card.dataset.variantId,
        price: item.price,
        mode: isMulti ? 'multi' : 'single'
      });

      this.syncUi();
    }

    getCardImageSrc(card) {
      const img = card.querySelector('.bundle-builder__card-image');
      if (img && img.currentSrc) return img.currentSrc;
      if (img && img.src) return img.src;
      return null;
    }

    /* ---------------- Stepper / Footer actions ---------------- */

    onStepperClick(evt) {
      const btn = evt.currentTarget;
      const target = parseInt(btn.dataset.stepIndex, 10);
      // Only allow jumping to a step if all prior selection steps have a selection or are skipped
      if (!this.canJumpTo(target)) return;
      this.goToStep(target);
    }

    canJumpTo(target) {
      for (let i = 0; i < target; i += 1) {
        const stepEl = this.selectionSteps[i];
        if (!stepEl) continue;
        const skippable = stepEl.dataset.skippable === 'true';
        const isMulti = stepEl.dataset.selectionMode === 'multi';
        if (isMulti) continue; // multi steps are always passable
        const sel = this.state.selections[i];
        const hasSel = sel && (sel.variantId || sel.skipped || (sel.multi && sel.items && sel.items.length));
        if (!hasSel && !skippable) return false;
      }
      return true;
    }

    onFooterClick(evt) {
      const btn = evt.target.closest('[data-action]');
      if (!btn) return;
      const action = btn.dataset.action;
      if (action === 'next') this.handleNext();
      else if (action === 'back') this.handleBack();
      else if (action === 'skip') this.handleSkip();
      else if (action === 'add-to-cart') this.handleAddToCart();
    }

    handleNext() {
      const cur = this.state.currentStep;
      if (cur < this.totalSteps - 1) this.goToStep(cur + 1);
    }

    handleBack() {
      const cur = this.state.currentStep;
      if (cur > 0) this.goToStep(cur - 1);
    }

    handleSkip() {
      const cur = this.state.currentStep;
      const stepEl = this.selectionSteps[cur];
      if (!stepEl || stepEl.dataset.skippable !== 'true') return;

      // Clear visual selections in this step
      stepEl.querySelectorAll('.bundle-builder__card[aria-checked="true"]').forEach((c) => {
        c.setAttribute('aria-checked', 'false');
      });

      this.state.selections[cur] = { skipped: true, role: stepEl.dataset.role || 'accessory' };
      this.saveState();
      this.handleNext();
    }

    /* ---------------- UI sync ---------------- */

    syncUi() {
      const cur = this.state.currentStep;
      const isSummary = this.isSummaryStep(cur);
      const curStepEl = this.selectionSteps[cur];
      const curSel = this.state.selections[cur];
      const isMulti = !isSummary && curStepEl && curStepEl.dataset.selectionMode === 'multi';
      const hasSelection = !isSummary && curSel && (
        curSel.variantId || (curSel.multi && curSel.items && curSel.items.length > 0)
      );
      const isSkippable = !isSummary && curStepEl && curStepEl.dataset.skippable === 'true';

      if (this.backBtn) this.backBtn.hidden = cur === 0;
      // Hide the skip button for multi (multi is always passable via "Next")
      if (this.skipBtn) this.skipBtn.hidden = isMulti || !isSkippable || hasSelection;
      if (this.nextBtn) {
        this.nextBtn.hidden = isSummary;
        // Multi steps: always enabled. Single: enabled only with a pick or skip.
        this.nextBtn.disabled = !isSummary && !isMulti && !hasSelection && !(curSel && curSel.skipped);
      }
      if (this.ctaBtn) this.ctaBtn.hidden = !isSummary;

      // Restore visual checked state for cards that match persisted selection
      this.selectionSteps.forEach((stepEl) => {
        const idx = parseInt(stepEl.dataset.stepIndex, 10);
        const sel = this.state.selections[idx];
        const stepIsMulti = stepEl.dataset.selectionMode === 'multi';
        stepEl.querySelectorAll('.bundle-builder__card').forEach((card) => {
          let match = false;
          if (sel && !sel.skipped) {
            if (stepIsMulti && sel.multi && Array.isArray(sel.items)) {
              match = sel.items.some(
                (it) => String(it.variantId) === String(card.dataset.variantId)
              );
            } else if (!stepIsMulti) {
              match = String(sel.variantId) === String(card.dataset.variantId);
            }
          }
          card.setAttribute('aria-checked', match ? 'true' : 'false');
          // Make cards focusable within the current step only
          card.tabIndex = idx === cur && card.getAttribute('aria-disabled') !== 'true' ? 0 : -1;
        });
      });

      // Footer totals reflect all selections so far
      const totals = this.calculateTotals();
      if (this.footerCountEl) this.footerCountEl.textContent = String(totals.itemCount);
      if (this.footerTotalEl) this.footerTotalEl.textContent = formatMoney(totals.total);
      const hasSavings = totals.compareTotal > totals.total;
      if (this.footerCompareEl) {
        this.footerCompareEl.textContent = hasSavings ? formatMoney(totals.compareTotal) : '';
        this.footerCompareEl.style.display = hasSavings ? '' : 'none';
      }
      if (this.footerSavingsEl) {
        if (hasSavings && totals.compareTotal > 0) {
          const pct = Math.round((totals.compareTotal - totals.total) / totals.compareTotal * 100);
          this.footerSavingsEl.textContent = 'Du sparst ' + pct + ' %';
          this.footerSavingsEl.style.display = '';
        } else {
          this.footerSavingsEl.style.display = 'none';
        }
      }
    }

    /* ---------------- Totals & summary ---------------- */

    calculateTotals() {
      const selected = [];
      Object.keys(this.state.selections).forEach((k) => {
        const sel = this.state.selections[k];
        if (!sel || sel.skipped) return;
        if (sel.multi && Array.isArray(sel.items)) {
          sel.items.forEach((item) => {
            selected.push(Object.assign({}, item, { role: sel.role || 'accessory' }));
          });
        } else if (sel.variantId) {
          selected.push(sel);
        }
      });
      const itemCount = selected.length;
      const subtotal = selected.reduce((sum, s) => sum + (s.price || 0), 0);
      const compareTotal = selected.reduce(
        (sum, s) => sum + (s.comparePrice && s.comparePrice > s.price ? s.comparePrice : s.price || 0),
        0
      );
      const compareSavings = Math.max(0, compareTotal - subtotal);
      const discountPct = this.discountPctFor(itemCount);
      const discount = Math.round(subtotal * (discountPct / 100));
      const total = subtotal - discount;
      const totalSavings = compareSavings + discount;
      return {
        itemCount,
        subtotal,
        compareTotal,
        compareSavings,
        discount,
        discountPct,
        totalSavings,
        total,
        selected
      };
    }

    discountPctFor(count) {
      if (count >= 4) return this.discount4;
      if (count === 3) return this.discount3;
      if (count === 2) return this.discount2;
      return 0;
    }

    renderSummary() {
      if (!this.summaryListEl) return;
      const totals = this.calculateTotals();
      const showDiscountBanner = this.dataset.showDiscountBanner === 'true';

      // Build list
      this.summaryListEl.innerHTML = '';
      this.selectionSteps.forEach((stepEl) => {
        const idx = parseInt(stepEl.dataset.stepIndex, 10);
        const sel = this.state.selections[idx];
        if (!sel) return;
        if (sel.skipped) {
          this.summaryListEl.appendChild(
            this.buildSkippedSummaryItem(stepHeading(stepEl))
          );
          return;
        }
        if (sel.multi && Array.isArray(sel.items)) {
          sel.items.forEach((item) => {
            this.summaryListEl.appendChild(this.buildSummaryItem(item));
          });
          return;
        }
        if (sel.variantId) {
          this.summaryListEl.appendChild(this.buildSummaryItem(sel));
        }
      });

      // Totals
      const hasRegular = totals.compareTotal > totals.subtotal;
      if (this.summaryRegularRow) this.summaryRegularRow.hidden = !hasRegular;
      if (this.summaryRegularEl) this.summaryRegularEl.textContent = formatMoney(totals.compareTotal);

      const savingsToShow = totals.totalSavings;
      const hasSavings = savingsToShow > 0;
      if (this.summarySavingsRow) this.summarySavingsRow.hidden = !hasSavings;
      if (this.summarySavingsEl) this.summarySavingsEl.textContent = '-' + formatMoney(savingsToShow);

      if (this.summaryTotalEl) this.summaryTotalEl.textContent = formatMoney(totals.total);

      if (showDiscountBanner) {
        const hasDiscount = totals.discount > 0;
        if (this.discountBannerEl) this.discountBannerEl.hidden = !hasDiscount;
        if (this.summaryDiscountRow) this.summaryDiscountRow.hidden = !hasDiscount;
        if (hasDiscount) {
          if (this.discountLabelEl) {
            this.discountLabelEl.textContent = tGet(this, 'bundle.summary.discount_label', 'Bundle-Rabatt')
              + ' (' + totals.discountPct + '%)';
          }
          if (this.discountValueEl) this.discountValueEl.textContent = '-' + formatMoney(totals.discount);
          if (this.summaryDiscountEl) this.summaryDiscountEl.textContent = '-' + formatMoney(totals.discount);
        }
      }

      this.emit('bundle:completed', {
        items: totals.selected.map((s) => ({
          productId: s.productId,
          variantId: s.variantId,
          price: s.price
        })),
        subtotal: totals.subtotal,
        discount: totals.discount,
        total: totals.total
      });
    }

    buildSummaryItem(item) {
      const li = document.createElement('li');
      li.className = 'bundle-builder__summary-item';
      const imgHtml = item.productImage
        ? '<div class="bundle-builder__summary-item-media"><img src="' +
          escapeAttr(item.productImage) +
          '" alt=""></div>'
        : '<div class="bundle-builder__summary-item-media"></div>';
      const variantLine =
        item.variantTitle && item.variantTitle !== 'Default Title'
          ? '<span class="bundle-builder__summary-item-variant">' +
            escapeHtml(item.variantTitle) +
            '</span>'
          : '';
      const hasSale = item.comparePrice && item.comparePrice > item.price;
      const priceHtml = hasSale
        ? '<span class="bundle-builder__summary-item-price bundle-builder__summary-item-price--sale">' +
          '<s class="bundle-builder__summary-item-compare">' +
          formatMoney(item.comparePrice) +
          '</s>' +
          '<span class="bundle-builder__summary-item-current">' +
          formatMoney(item.price || 0) +
          '</span>' +
          '</span>'
        : '<span class="bundle-builder__summary-item-price">' +
          formatMoney(item.price || 0) +
          '</span>';
      li.innerHTML =
        imgHtml +
        '<div class="bundle-builder__summary-item-body">' +
        '<p class="bundle-builder__summary-item-title">' +
        escapeHtml(item.productTitle || '') +
        '</p>' +
        variantLine +
        '</div>' +
        priceHtml;
      return li;
    }

    buildSkippedSummaryItem(label) {
      const li = document.createElement('li');
      li.className = 'bundle-builder__summary-item bundle-builder__summary-item--skipped';
      li.innerHTML =
        '<div class="bundle-builder__summary-item-body">' +
        '<p class="bundle-builder__summary-item-title">' +
        escapeHtml(label || '') +
        '</p>' +
        '<span class="bundle-builder__summary-item-variant">' +
        escapeHtml(tGet(this, 'bundle.summary.skipped', 'Übersprungen')) +
        '</span>' +
        '</div>';
      return li;
    }

    /* ---------------- Cart add ---------------- */

    async handleAddToCart() {
      const totals = this.calculateTotals();
      if (totals.itemCount === 0) {
        this.showError(tGet(this, 'bundle.error.no_selection', 'Bitte wähle mindestens ein Produkt.'));
        return;
      }

      const idleLabel = this.ctaBtn.querySelector('[data-cta-idle]');
      const busyLabel = this.ctaBtn.querySelector('[data-cta-busy]');
      if (idleLabel) idleLabel.hidden = true;
      if (busyLabel) busyLabel.hidden = false;
      this.ctaBtn.disabled = true;
      this.hideError();

      // Carrier mode: add one fixed variant at the bundle price; encode all
      // selections as line-item properties so fulfillment knows what to pack.
      // Standard mode: each selection becomes its own line item.
      let items;
      if (this.carrierVariantId) {
        const props = { _bundle_id: this.state.bundleId };
        totals.selected.forEach((sel) => {
          const role = sel.role || 'accessory';
          const label = (sel.productTitle || '') +
            (sel.variantTitle && sel.variantTitle !== 'Default Title'
              ? ' – ' + sel.variantTitle
              : '');
          props['_bundle_' + role] = label;
          props['_bundle_' + role + '_vid'] = String(sel.variantId);
        });
        items = [{ id: parseInt(this.carrierVariantId, 10), quantity: 1, properties: props }];
      } else {
        items = totals.selected.map((sel) => ({
          id: parseInt(sel.variantId, 10),
          quantity: 1,
          properties: {
            _bundle_id: this.state.bundleId,
            _bundle_role: sel.role || 'accessory'
          }
        }));
      }

      // Fixed variant (e.g. a stall) always prepended to every bundle order.
      if (this.fixedVariantId) {
        items.unshift({
          id: parseInt(this.fixedVariantId, 10),
          quantity: 1,
          properties: { _bundle_id: this.state.bundleId, _bundle_role: 'fixed' }
        });
      }

      const routes = (window.theme && window.theme.routes) || {};
      const cartAddUrl = (routes.cartAdd || '/cart/add') + '.js';

      const payload = {
        items: items,
        sections: ['cart-drawer', 'cart-icon-bubble'],
        sections_url: window.location.pathname
      };

      try {
        const res = await fetch(cartAddUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.description || data.message || 'Cart add failed');
        }

        this.emit('bundle:added_to_cart', {
          bundleId: this.state.bundleId,
          items: items
        });

        // Clear bundle state so a fresh bundle starts on next visit, then
        // redirect to the cart page.
        sessionStorage.removeItem(STORAGE_KEY);
        const cartUrl = routes.cart || '/cart';
        window.location.href = cartUrl;
        return;
      } catch (err) {
        this.showError(
          (err && err.message) ||
            tGet(this, 'bundle.error.generic', 'Bundle konnte nicht hinzugefügt werden.')
        );
      } finally {
        if (idleLabel) idleLabel.hidden = false;
        if (busyLabel) busyLabel.hidden = true;
        this.ctaBtn.disabled = false;
      }
    }

    resetBundle() {
      this.state = {
        v: STATE_VERSION,
        bundleId: uuid(),
        currentStep: 0,
        selections: {}
      };
      this.saveState();
      this.goToStep(0, { replace: true, emit: false });
    }

    /* ---------------- Helpers ---------------- */

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = false;
    }

    hideError() {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    emit(name, detail) {
      window.dispatchEvent(new CustomEvent(name, { detail: detail }));
    }
  }

  /* ---------------- Free functions ---------------- */

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;');
  }

  function stepHeading(stepEl) {
    const h = stepEl.querySelector('.bundle-builder__step-heading');
    return h ? h.textContent.trim() : '';
  }

  // Best-effort translation helper: reads a data-i18n-<key> attribute from the
  // element, otherwise returns the fallback. Keeps JS out of the locale system
  // except for the handful of strings it generates dynamically.
  function tGet(el, key, fallback) {
    try {
      const attr = 'data-i18n-' + key.replace(/\./g, '-');
      const v = el.getAttribute(attr);
      return v || fallback;
    } catch (e) {
      return fallback;
    }
  }

  if (!customElements.get('bundle-builder')) {
    customElements.define('bundle-builder', BundleBuilder);
  }
})();
